import { createApi } from '@reduxjs/toolkit/query/react'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { CMS_FETCH_DRAFTS, CMS_URL, IS_PRODUCTION } from 'constants/envs'
import {
  CmsVideoTestimonial,
  GetFaqByMultipleSlugsPayload,
  GetFaqByMultipleSlugsResponse,
  GetFaqBySlugPayload,
  GetFaqBySlugResponse,
  GetMinimumAppVersionResponse,
  GetProducerContactResponse,
  GetProductsContentResponse,
  ProducerContactCmsResponse,
  ProductsCmsResponse,
  RawFaqCategoryCmsResponse,
  RawReferralActionData,
  StrapiCMSResponse
} from './types'
import { stringify } from 'qs'
import { LanguageLowercase } from 'store/app/types'
import { convertProductsCmsResponseToDictionary } from 'utils/products'
import { FAQ_SLUGS } from 'constants/faq.ts'
import dayjs from 'dayjs'
import { Language, PRODUCTS } from '@boltenergy-be/shared'
import { FaqCategory, StrapiObject } from 'types/cms.ts'

export const cmsApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: `${CMS_URL}/api`
  }),
  endpoints: (builder) => ({
    // GET FAQ BY SLUG(S)
    getFaqBySlug: builder.query<GetFaqBySlugResponse, GetFaqBySlugPayload>({
      query: ({ slug, language }) => {
        const query = stringify(
          {
            populate: 'articles',
            locale: language.toLowerCase(),
            filters: {
              slug: {
                $eq: FAQ_SLUGS[slug][language.toLowerCase() as LanguageLowercase]
              }
            }
          },
          {
            skipNulls: true,
            addQueryPrefix: true,
            encodeValuesOnly: true
          }
        )

        return {
          url: `/faq-categories${query}`
        }
      },
      transformResponse: ({ data }: StrapiCMSResponse<RawFaqCategoryCmsResponse>) => {
        const transformedData: FaqCategory | undefined = data?.length
          ? {
              ...data[0],
              questions: data[0].articles
            }
          : undefined

        return { category: transformedData }
      }
    }),

    // GET FAQ BY SLUG(S)
    getFaqByMultipleSlugs: builder.query<GetFaqByMultipleSlugsResponse, GetFaqByMultipleSlugsPayload>({
      query: ({ slugs, language }) => {
        const query = stringify(
          {
            locale: language,
            filters: {
              slug: {
                $in: slugs.map((slug) => FAQ_SLUGS[slug][language.toLowerCase() as LanguageLowercase])
              }
            }
          },
          {
            skipNulls: true,
            addQueryPrefix: true,
            encodeValuesOnly: true
          }
        )

        return {
          url: `/faq-categories${query}`
        }
      },
      transformResponse: ({ data }: StrapiCMSResponse<RawFaqCategoryCmsResponse>) => {
        const transformedData: FaqCategory[] = data.map((faq) => ({
          ...faq,
          questions: faq.articles.filter((article) => !!article.isNewPortal)
        }))

        return { categories: transformedData }
      }
    }),

    // GET PRODUCTS CONTENT
    getProductContent: builder.query<GetProductsContentResponse, { language: Language }>({
      query: ({ language }) => ({
        method: 'GET',
        url: `products?locale=${language.toLowerCase()}`
      }),
      transformResponse: ({ data }: StrapiCMSResponse<ProductsCmsResponse>) => {
        const productsContent = convertProductsCmsResponseToDictionary(data)

        // TODO: temporary fix for the direct debit mandatory copy
        if (productsContent?.[PRODUCTS.variableOnline]?.perks) {
          const variableOnlinePerks = productsContent[PRODUCTS.variableOnline]!.perks
          variableOnlinePerks?.[0].children.pop()
          productsContent[PRODUCTS.variableOnline]!.perks = variableOnlinePerks
        }

        return productsContent
      }
    }),

    // GET PRODUCER CONTACT
    getProducerContact: builder.query<GetProducerContactResponse, { contactId: string }>({
      query: ({ contactId }) => ({
        method: 'GET',
        url: `producer-contact/${contactId}`
      }),
      transformResponse: (res: StrapiCMSResponse<ProducerContactCmsResponse>) => {
        return {
          contact: res.data
        }
      }
    }),

    // GET PRODUCER CONTACT
    getVideoTestimonials: builder.query<CmsVideoTestimonial[], void>({
      query: () => ({
        method: 'GET',
        url: `video-testimonials`
      }),
      transformResponse: (res: StrapiCMSResponse<CmsVideoTestimonial[]>) => {
        return res.data?.sort((a, b) => b.id - a.id) || []
      }
    }),

    // REFERRAL ACTIONS
    getActiveReferralAction: builder.query<RawReferralActionData | null, { language: Language }>({
      query: ({ language }) => {
        const query = stringify(
          {
            locale: language,
            status: !IS_PRODUCTION && CMS_FETCH_DRAFTS?.split(',').includes('referral-actions') ? 'draft' : 'published'
          },
          {
            skipNulls: true,
            addQueryPrefix: true,
            encodeValuesOnly: true
          }
        )

        return '/referral-actions' + query
      },
      transformResponse: ({ data }: StrapiCMSResponse<RawReferralActionData[]>) => {
        const today = dayjs()
        const currentlyActiveAction = data.find((action) =>
          today.isBetween(dayjs(action.start).startOf('day'), dayjs(action.end).endOf('day'))
        )

        return currentlyActiveAction || null
      }
    }),

    // MINIMUM APP VERSION
    getMinimumAppVersion: builder.query<GetMinimumAppVersionResponse, null>({
      query: () => '/minimum-app-version',
      transformResponse: ({ data }: StrapiCMSResponse<StrapiObject<GetMinimumAppVersionResponse>>) => {
        return {
          android: data.android,
          ios: data.ios
        }
      },
      keepUnusedDataFor: 60 * 60 // 1 hour
    })
  }),
  reducerPath: 'cmsApi'
})

export const {
  useGetFaqBySlugQuery,
  useGetFaqByMultipleSlugsQuery,
  useGetProductContentQuery,
  useGetProducerContactQuery,
  useGetActiveReferralActionQuery,
  useGetVideoTestimonialsQuery,
  useGetMinimumAppVersionQuery
} = cmsApi
